import { useSearchStore } from "../stores/searchStore";
import { useNavigate } from "react-router";
import { useOrgStore } from "../stores/orgStore";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { MessageSquare, User, Clock, Hash, ExternalLink } from "lucide-react";

export function MessageDetailModal() {
  const { selectedMessage, setSelectedMessage } = useSearchStore();
  const navigate = useNavigate();
  const { currentOrg } = useOrgStore();

  const handleViewConversation = () => {
    if (!selectedMessage || !currentOrg) return;

    const chatId = selectedMessage.metadata.chat_id;
    if (chatId) {
      // Navigate to summaries page filtered by this conversation
      navigate(`/org/${currentOrg}/app/inbox?conversation=${chatId}`);
      setSelectedMessage(null);
    }
  };

  if (!selectedMessage) return null;

  const { metadata } = selectedMessage;
  const timestamp = metadata.timestamp ? new Date(metadata.timestamp) : null;

  return (
    <Dialog open={!!selectedMessage} onOpenChange={() => setSelectedMessage(null)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Message Details
          </DialogTitle>
          <DialogDescription>
            {metadata.chat_name || "Conversation"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Message Content */}
          <div className="rounded-lg border border-gray-200 dark:border-border bg-gray-50 dark:bg-muted p-4">
            <p className="text-sm leading-relaxed text-gray-900 dark:text-gray-100">
              {selectedMessage.content}
            </p>
          </div>

          {/* Metadata Grid */}
          <div className="grid grid-cols-2 gap-4">
            {metadata.sender_name && (
              <div className="flex items-start gap-2">
                <User className="h-4 w-4 text-gray-500 dark:text-gray-400 mt-0.5" />
                <div>
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Sender</div>
                  <div className="text-sm text-gray-900 dark:text-gray-100">{metadata.sender_name}</div>
                  {metadata.sender_phone && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">{metadata.sender_phone}</div>
                  )}
                </div>
              </div>
            )}

            {timestamp && (
              <div className="flex items-start gap-2">
                <Clock className="h-4 w-4 text-gray-500 dark:text-gray-400 mt-0.5" />
                <div>
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Time</div>
                  <div className="text-sm text-gray-900 dark:text-gray-100">
                    {timestamp.toLocaleDateString()} at {timestamp.toLocaleTimeString()}
                  </div>
                </div>
              </div>
            )}

            {metadata.chat_type && (
              <div className="flex items-start gap-2">
                <Hash className="h-4 w-4 text-gray-500 dark:text-gray-400 mt-0.5" />
                <div>
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Chat Type</div>
                  <div className="text-sm text-gray-900 dark:text-gray-100 capitalize">
                    {metadata.is_dm ? "Direct Message" : metadata.chat_type}
                  </div>
                </div>
              </div>
            )}

            {metadata.platform && (
              <div className="flex items-start gap-2">
                <MessageSquare className="h-4 w-4 text-gray-500 dark:text-gray-400 mt-0.5" />
                <div>
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Platform</div>
                  <div className="text-sm text-gray-900 dark:text-gray-100 capitalize">
                    {metadata.platform}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setSelectedMessage(null)}>
            Close
          </Button>
          {metadata.chat_id && (
            <Button onClick={handleViewConversation}>
              <ExternalLink className="h-4 w-4 mr-2" />
              View Conversation
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
